import React from 'react'
import { fetchAiObservability } from '../../../lib/nudgeApi'
import type { AiObservability } from '../../../types/api'

const NUDGE_STATUSES = [
  'PENDING', 'AI_PROCESSING', 'AI_READY', 'SENDING',
  'SENT', 'FAILED', 'REPLY_RECEIVED', 'CONV_ACTIVE',
  'RECOVERED', 'RETURNED', 'NO_SHOW', 'OPT_OUT', 'EXHAUSTED',
]

export function AdminAiHealthPage() {
  const [data, setData] = React.useState<AiObservability | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const load = React.useCallback(() => {
    setLoading(true)
    setError(null)
    fetchAiObservability()
      .then(setData)
      .catch(() => setError('Failed to load AI observability.'))
      .finally(() => setLoading(false))
  }, [])

  React.useEffect(() => {
    load()
    const handle = window.setInterval(load, 30000)
    return () => window.clearInterval(handle)
  }, [load])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="panel">
        <div className="panel-header">
          <div>
            <div className="panel-title">Queue health</div>
            <div className="panel-sub">Live nudge_queue depth, stale rows, and recent failures</div>
          </div>
          <button
            className="panel-action"
            onClick={load}
            disabled={loading}
            style={{ opacity: loading ? 0.5 : 1 }}
          >
            Refresh
          </button>
        </div>
        <div style={{ padding: '12px 20px 20px' }}>
          {loading && !data && <div style={{ fontSize: 13, color: 'var(--text3)' }}>Loading…</div>}
          {error && (
            <div>
              <div style={{ fontSize: 13, color: 'var(--red)', marginBottom: 8 }}>{error}</div>
              <button className="btn-primary" onClick={load} style={{ padding: '4px 12px', fontSize: 12 }}>Retry</button>
            </div>
          )}
          {data && <QueueHealthSection data={data} />}
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <div>
            <div className="panel-title">AI server metrics</div>
            <div className="panel-sub">Proxied from nudge-ai-server /metrics endpoint</div>
          </div>
        </div>
        <div style={{ padding: '12px 20px 20px' }}>
          {data && <AiMetricsSection ai={data.ai} />}
        </div>
      </div>
    </div>
  )
}

function MetricCard({ label, value, sub, accent }: { label: string; value: React.ReactNode; sub?: React.ReactNode; accent?: string }) {
  return (
    <div style={{
      padding: '12px 14px',
      background: 'var(--surface2)',
      borderRadius: 8,
      border: `1px solid ${accent ?? 'var(--border)'}`,
    }}>
      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'DM Mono', monospace", letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{
        fontSize: 22, fontWeight: 600,
        color: accent ?? 'var(--text)',
        fontFamily: "'DM Mono', monospace",
        marginTop: 4,
      }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function QueueHealthSection({ data }: { data: AiObservability }) {
  const q = data.queue
  const staleAlert = q.staleAiProcessing > 0
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <MetricCard
          label="Stale AI rows"
          value={q.staleAiProcessing}
          sub={`> ${q.staleThresholdMinutes}m claimed`}
          accent={staleAlert ? 'var(--red)' : undefined}
        />
        <MetricCard label="Sent 24h" value={q.sentLast24h} />
        <MetricCard
          label="Failed 24h"
          value={q.failedLast24h}
          accent={q.failedLast24h > 0 ? 'var(--orange)' : undefined}
        />
        <MetricCard
          label="Total FAILED"
          value={Object.values(q.failedByStage).reduce((a, b) => a + b, 0)}
        />
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          Depth by status
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {NUDGE_STATUSES.map(s => {
            const c = q.depthByStatus[s] ?? 0
            if (c === 0) return null
            return (
              <div key={s} style={{
                padding: '4px 10px',
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: 12,
                fontSize: 12,
                fontFamily: "'DM Mono', monospace",
              }}>
                <span style={{ color: 'var(--text3)' }}>{s}</span>{' '}
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{c}</span>
              </div>
            )
          })}
        </div>
      </div>

      {Object.keys(q.failedByStage).length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Failed by stage
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {Object.entries(q.failedByStage).map(([stage, count]) => (
              <div key={stage} style={{
                padding: '4px 10px',
                background: 'var(--red)18',
                border: '1px solid var(--red)44',
                borderRadius: 12,
                fontSize: 12,
                fontFamily: "'DM Mono', monospace",
              }}>
                <span style={{ color: 'var(--red)' }}>{stage}</span>{' '}
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          Recent failures
        </div>
        {q.recentFailures.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>No failures.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Business', 'Stage', 'Retries', 'Retry after', 'When'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 11, color: 'var(--text3)', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {q.recentFailures.map(f => (
                  <tr key={f.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 8px', color: 'var(--text)' }}>{f.businessName}</td>
                    <td style={{ padding: '6px 8px', fontFamily: "'DM Mono', monospace", color: 'var(--red)' }}>{f.failedStage ?? '—'}</td>
                    <td style={{ padding: '6px 8px', fontFamily: "'DM Mono', monospace", color: 'var(--text2)' }}>{f.retryCount}</td>
                    <td style={{ padding: '6px 8px', fontFamily: "'DM Mono', monospace", color: 'var(--text3)' }}>
                      {f.retryAfter ? new Date(f.retryAfter).toLocaleString() : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', fontFamily: "'DM Mono', monospace", color: 'var(--text3)' }}>
                      {new Date(f.updatedAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          Recent generated subjects
        </div>
        {q.recentSamples.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>No sent emails yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {q.recentSamples.map(s => (
              <div key={s.id} style={{
                padding: '8px 12px',
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 13,
              }}>
                <div style={{ color: 'var(--text)', marginBottom: 2 }}>{s.subject}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>
                  {s.businessName} · {new Date(s.sentAt).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function AiMetricsSection({ ai }: { ai: AiObservability['ai'] }) {
  if (!ai.available) {
    return (
      <div style={{ fontSize: 13, color: 'var(--text3)', padding: '8px 0' }}>
        AI server is unreachable at <code style={{ fontFamily: "'DM Mono', monospace", color: 'var(--text2)' }}>{ai.source}</code>.
        Check that <code style={{ fontFamily: "'DM Mono', monospace" }}>nudge-ai-server</code> is running and that
        <code style={{ fontFamily: "'DM Mono', monospace" }}> NUDGE_AI_METRICS_URL</code> is configured correctly.
      </div>
    )
  }

  const totalGrounding = Object.values(ai.groundingRejections).reduce((a, b) => a + b, 0)
  const totalLint = Object.values(ai.lintViolations).reduce((a, b) => a + b, 0)
  const avgCache = Object.values(ai.promptCacheHitRatio)
  const cacheAvg = avgCache.length > 0 ? avgCache.reduce((a, b) => a + b, 0) / avgCache.length : 0

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        <MetricCard
          label="Grounding rejections"
          value={totalGrounding}
          sub={ai.groundingRejections.retry
            ? `${ai.groundingRejections.retry ?? 0} on retry`
            : 'no rejections'}
          accent={totalGrounding > 0 ? 'var(--orange)' : undefined}
        />
        <MetricCard
          label="Lint violations"
          value={totalLint}
          accent={totalLint > 0 ? 'var(--orange)' : undefined}
        />
        <MetricCard
          label="Avg cache hit rate"
          value={`${(cacheAvg * 100).toFixed(1)}%`}
          sub={`${avgCache.length} path/model pairs`}
        />
      </div>

      <RatioBars
        title="Persona field population"
        rows={Object.entries(ai.personaPopulationRatio).sort((a, b) => b[1] - a[1])}
        suffix={v => `${(v * 100).toFixed(0)}%`}
        warnBelow={0.3}
      />

      {Object.keys(ai.promptCacheHitRatio).length > 0 && (
        <RatioBars
          title="Prompt cache hit ratio"
          rows={Object.entries(ai.promptCacheHitRatio)}
          suffix={v => `${(v * 100).toFixed(1)}%`}
        />
      )}

      {Object.keys(ai.claimsByPath).length > 0 && (
        <CountList title="Claims (lifetime)" rows={ai.claimsByPath} />
      )}

      {Object.keys(ai.processedByPathOutcome).length > 0 && (
        <CountList title="Processed by outcome" rows={ai.processedByPathOutcome} />
      )}

      {Object.keys(ai.intentClassification).length > 0 && (
        <CountList title="Intent classification" rows={ai.intentClassification} />
      )}

      {Object.keys(ai.lintViolations).length > 0 && (
        <CountList title="Lint violations by check" rows={ai.lintViolations} />
      )}
    </div>
  )
}

function RatioBars({
  title,
  rows,
  suffix,
  warnBelow,
}: {
  title: string
  rows: [string, number][]
  suffix: (v: number) => string
  warnBelow?: number
}) {
  if (rows.length === 0) return null
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {rows.map(([k, v]) => {
          const warn = warnBelow !== undefined && v < warnBelow
          return (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 180, fontSize: 12, fontFamily: "'DM Mono', monospace", color: 'var(--text2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {k}
              </div>
              <div style={{ flex: 1, height: 6, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${v * 100}%`, height: '100%', background: warn ? 'var(--orange)' : 'var(--accent)' }} />
              </div>
              <div style={{ width: 56, textAlign: 'right', fontSize: 12, fontFamily: "'DM Mono', monospace", color: warn ? 'var(--orange)' : 'var(--text)' }}>
                {suffix(v)}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function CountList({ title, rows }: { title: string; rows: Record<string, number> }) {
  const entries = Object.entries(rows).sort((a, b) => b[1] - a[1])
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {entries.map(([k, v]) => (
          <div key={k} style={{
            padding: '4px 10px',
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            fontSize: 12,
            fontFamily: "'DM Mono', monospace",
          }}>
            <span style={{ color: 'var(--text3)' }}>{k}</span>{' '}
            <span style={{ color: 'var(--text)', fontWeight: 600 }}>{Math.round(v)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
